<!--
  WizardModal.vue
  
  Purpose: Generic, reusable wizard component for multi-step workflows
  
  Responsibilities:
  - Provides wizard UI with step indicators (stepper) and step content panels
  - Manages step navigation (next, previous, go to step)
  - Handles step validation before transitions
  - Supports both slot-based and dynamic step rendering via stepRegistry
  - Provides button labels and states based on step position
  - Emits events for step changes, validation, completion
  
  Architecture:
  - Uses useWizard composable for internal wizard state management
  - Supports two rendering modes:
    1. Slot-based (legacy): Steps rendered via named slots (#step-0, #step-1, etc.)
    2. Dynamic (new): Steps rendered from stepRegistry prop (automatic component resolution)
  - Dynamic mode enables complex wizards with conditional steps and automatic props/events binding
  
  Usage:
  - Simple wizards: Use slot-based rendering with steps array
  - Complex wizards: Use stepRegistry prop with dynamicSteps=true for automatic rendering
  
  Key Features:
  - Linear/non-linear navigation modes
  - Step validation before transitions
  - Custom button labels and icons per step
  - Loading states
  - Summary display
  - Pre-wizard and completion screens
-->
<template>
  <Modal :show="showModal" :title="modalTitle" :size="size"
    :modal-size="modalSize" :modal-size-mobile="modalSizeMobile"
    :elem-id="elemId"
    :close-test-id="closeTestId"
    :save-test-id="saveTestId"
    :close-label="computedBackLabel" :close-icon="computedBackIcon" :save-label="computedNextLabel"
    :save-icon="computedNextIcon" :save-fn="computedNextFn" :save-disabled-fn="computedDisableNextFn"
    @close="handleBackButton" @dismiss="handleDismiss">
    <div class="wizard-modal-content">
      <slot v-if="showPreWizard" name="pre-wizard"></slot>

      <!-- Main wizard content -->
      <div v-else class="wizard-modal-container">
        <!-- Summary section (optional) -->
        <div v-if="showSummary">
          <!-- Custom summary slot (if provided) -->
          <slot v-if="$slots.summary" name="summary" :data="summaryData"></slot>

          <!-- Built-in summary template -->
          <div v-else-if="summaryItems && summaryItems.length > 0" class="wizard-summary">
            <div class="summary-row">
              <div v-for="(item, index) in summaryItems" :key="index" class="summary-item">
                <i v-if="item.icon" :class="item.icon"></i>
                <span class="summary-label">{{ item.label }}:</span>
                <span class="summary-value">{{ item.value }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Step indicators and content -->
        <WizardSteps :steps="steps" :current-step="wizard.currentStep.value" :show-description="showStepDescription"
          :allow-click="allowStepClick" :orientation="computedOrientation" @step-click="handleStepClick">
          <!-- Dynamic step rendering from registry OR slot-based (backward compatible) -->
          <template v-for="(step, index) in steps" :key="index" #[`step-${index}`]>
            <!-- Dynamic rendering from stepRegistry -->
            <component
              v-if="dynamicSteps && stepRegistry && resolveStepComponent(step.key || step.title, index)"
              :is="resolveStepComponent(step.key || step.title, index)!.component"
              v-bind="resolveStepComponent(step.key || step.title, index)!.props"
              v-on="resolveStepComponent(step.key || step.title, index)!.events"
            />
            <!-- Fallback to slot-based rendering (backward compatible) -->
            <slot
              v-else
              :name="`step-${index}`"
              :step="index"
              :stepConfig="step"
              :isFirstStep="index === 0"
              :isLastStep="index === steps.length - 1"
              :goToNext="wizard.nextStep"
              :goToPrevious="wizard.previousStep"
              :goToStep="wizard.goToStep"
              :goToStepByKey="goToStepByKey"
              :validateStep="wizard.validateCurrentStep"
              :data="stepData"
            ></slot>
          </template>
        </WizardSteps>

        <!-- Completion screen (optional) -->
        <slot v-if="showComplete" name="complete" :result="completeResult"></slot>
      </div>
    </div>
    <!-- Pre-wizard slot (optional) -->

  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from './Modal.vue';
import WizardSteps from './wizard/WizardSteps.vue';
import { useWizard, type WizardEmitFn } from '@/composables/useWizard.ts';
import { log } from '@/utils/DebugLogger.ts';

const { t } = useI18n();

type ModalSize = 'small' | 'medium' | 'modalMd' | 'large' | 'xlarge' | 'full' | 'compactDesktop' | 'formOptimized';

interface WizardStep {
  title: string;
  key?: string;
  validate?: () => boolean;
  [key: string]: any;
}

interface SummaryItem {
  icon?: string;
  label: string;
  value: any;
}

interface StepRegistryEntry {
  component: any;
  getProps?: (state: any, index: number) => Record<string, any>;
  getEvents?: (state: any, index: number, handlers: any) => Record<string, any>;
  condition?: (state: any) => boolean;
  validate?: () => boolean;
}

const props = withDefaults(defineProps<{
  elemId?: string;
  closeTestId?: string;
  saveTestId?: string;
  steps: WizardStep[];
  modelValue?: number;
  linear?: boolean;
  allowStepClick?: boolean;
  showModal: boolean;
  modalTitle?: string;
  size?: ModalSize;
  /** @deprecated */
  modalSize?: string;
  /** @deprecated */
  modalSizeMobile?: string;
  customButtons?: boolean;
  backLabel?: string | ((step: number) => string) | null;
  nextLabel?: string | ((step: number) => string) | null;
  backIcon?: string | ((step: number) => string) | null;
  nextIcon?: string | ((step: number) => string) | null;
  disableBack?: boolean | ((step: number) => boolean);
  disableNext?: boolean | ((step: number) => boolean);
  /** @deprecated */
  stepperType?: string;
  orientation?: 'auto' | 'horizontal' | 'vertical';
  showStepDescription?: boolean;
  showSummary?: boolean;
  summaryData?: Record<string, any>;
  summaryItems?: SummaryItem[];
  showPreWizard?: boolean;
  showComplete?: boolean;
  completeResult?: Record<string, any>;
  resetOnClose?: boolean;
  confirmOnClose?: boolean;
  loading?: boolean;
  stepData?: Record<string, any>;
  stepRegistry?: Record<string, StepRegistryEntry> | null;
  dynamicSteps?: boolean;
  wizardState?: Record<string, any>;
  eventHandlers?: Record<string, any>;
}>(), {
  elemId: '',
  closeTestId: '',
  saveTestId: '',
  modelValue: undefined,
  linear: true,
  allowStepClick: false,
  modalTitle: '',
  size: 'large',
  modalSize: '',
  modalSizeMobile: '',
  customButtons: false,
  backLabel: null,
  nextLabel: null,
  backIcon: null,
  nextIcon: null,
  disableBack: false,
  disableNext: false,
  stepperType: 'primevue',
  orientation: 'vertical',
  showStepDescription: true,
  showSummary: false,
  summaryData: () => ({}),
  summaryItems: () => [],
  showPreWizard: false,
  showComplete: false,
  completeResult: () => ({}),
  resetOnClose: true,
  confirmOnClose: false,
  loading: false,
  stepData: () => ({}),
  stepRegistry: null,
  dynamicSteps: false,
  wizardState: () => ({}),
  eventHandlers: () => ({}),
});

const emit = defineEmits<{
  'update:modelValue': [value: number];
  'step-change': [step: number];
  'before-step-change': [step: number];
  'step-validated': [step: number];
  'step-validation-failed': [step: number];
  'next': [];
  'back': [];
  'finish': [];
  'cancel': [];
  'open': [];
  'close': [];
  'reset': [];
  'complete': [];
}>();

// Initialize wizard composable
const wizard = useWizard(props, emit as WizardEmitFn);

// Device detection for auto orientation
const isMobile = ref<boolean>(false);

const checkMobile = (): void => {
  isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
  if (props.orientation === 'auto') {
    checkMobile();
    window.addEventListener('resize', checkMobile);
  }
});

onBeforeUnmount(() => {
  if (props.orientation === 'auto') {
    window.removeEventListener('resize', checkMobile);
  }
});

// Computed orientation based on prop or device detection
const computedOrientation = computed((): 'horizontal' | 'vertical' => {
  if (props.orientation === 'auto') {
    return isMobile.value ? 'vertical' : 'horizontal';
  }
  return props.orientation;
});

// Computed button properties
const computedBackLabel = computed((): string => {
  if (props.customButtons && props.backLabel) {
    return typeof props.backLabel === 'function'
      ? props.backLabel(wizard.currentStep.value)
      : props.backLabel;
  }

  // Default behavior
  if (wizard.isFirstStep.value) {
    return t('Cancelar');
  }
  return t('Voltar');
});

const computedBackIcon = computed((): string => {
  if (props.customButtons && props.backIcon) {
    return typeof props.backIcon === 'function'
      ? props.backIcon(wizard.currentStep.value)
      : props.backIcon;
  }

  // Default behavior
  if (wizard.isFirstStep.value) {
    return 'pi pi-times';
  }
  return 'pi pi-arrow-left';
});

const computedNextLabel = computed((): string => {
  if (props.customButtons && props.nextLabel) {
    return typeof props.nextLabel === 'function'
      ? props.nextLabel(wizard.currentStep.value)
      : props.nextLabel;
  }

  // Default behavior
  if (props.loading) {
    return t('Processando...');
  }
  if (wizard.isLastStep.value) {
    return t('Finalizar');
  }
  return t('Próximo');
});

const computedNextIcon = computed((): string => {
  if (props.customButtons && props.nextIcon) {
    return typeof props.nextIcon === 'function'
      ? props.nextIcon(wizard.currentStep.value)
      : props.nextIcon;
  }

  // Default behavior
  if (wizard.isLastStep.value) {
    return 'pi pi-check';
  }
  return 'pi pi-arrow-right';
});

const computedNextFn = computed(() => {
  return handleNextButton;
});

const computedDisableNextFn = (): boolean => {
  if (props.loading) {
    return true;
  }

  if (props.customButtons && props.disableNext) {
    return typeof props.disableNext === 'function'
      ? props.disableNext(wizard.currentStep.value)
      : props.disableNext;
  }

  // Default behavior - disable if validation fails
  return !wizard.validateCurrentStep();
};

// Methods
const handleBackButton = (): void => {
  log('WizardModal', 'handleBackButton', 'Footer back button clicked', { currentStep: wizard.currentStep.value });

  if (wizard.isFirstStep.value) {
    // On first step, cancel
    handleCancel();
  } else {
    // Otherwise, go to previous step
    wizard.previousStep();
  }
};

const handleDismiss = (): void => {
  log('WizardModal', 'handleDismiss', 'Modal dismissed (X button, outside click, or Escape)', { currentStep: wizard.currentStep.value });

  // Dismiss events (X button, outside click, Escape) always close/cancel the wizard
  handleCancel();
};

const handleNextButton = (): void => {
  log('WizardModal', 'handleNextButton', 'Button clicked', {
    currentStep: wizard.currentStep.value,
    totalSteps: wizard.totalSteps.value,
    isLastStep: wizard.isLastStep.value,
    stepsLength: props.steps?.length
  });

  if (wizard.isLastStep.value) {
    // On last step, finish
    log('WizardModal', 'handleNextButton', 'isLastStep=true, calling handleFinish');
    handleFinish();
  } else {
    // Otherwise, go to next step
    log('WizardModal', 'handleNextButton', 'isLastStep=false, calling nextStep');
    wizard.nextStep();
  }
};

const handleCancel = (): void => {
  log('WizardModal', 'handleCancel', 'Canceling wizard');

  if (props.confirmOnClose && wizard.currentStep.value > 0) {
    // TODO: Show confirmation dialog
    // For now, just close
  }

  emit('cancel');
  emit('close');

  if (props.resetOnClose) {
    wizard.reset();
  }
};

const handleFinish = (): void => {
  log('WizardModal', 'handleFinish', 'Finishing wizard');

  // Validate last step
  const isValid = wizard.validateCurrentStep();
  log('WizardModal', 'handleFinish', 'Validation result', { isValid });

  if (!isValid) {
    log('WizardModal', 'handleFinish', 'Validation FAILED, NOT emitting finish');
    return;
  }

  log('WizardModal', 'handleFinish', 'Emitting finish event');
  emit('finish');
};

const handleStepClick = (stepIndex: number): void => {
  log('WizardModal', 'handleStepClick', 'Step clicked', stepIndex);

  if (props.allowStepClick) {
    wizard.goToStep(stepIndex);
  }
};

/**
 * Resolve step component from registry
 *
 * @param stepKey - Step key or title to look up in registry
 * @param stepIndex - Current step index
 * @returns Component configuration with component, props, and events, or null if not found
 */
const resolveStepComponent = (stepKey: string, stepIndex: number): { component: any; props: Record<string, any>; events: Record<string, any> } | null => {
  if (!props.dynamicSteps || !props.stepRegistry) {
    return null;
  }

  // Try to find step by key first, then by title
  let config: StepRegistryEntry | undefined = props.stepRegistry[stepKey];

  // If not found by key, try to find by matching step title
  if (!config && stepKey) {
    const stepConfig = props.steps[stepIndex];
    if (stepConfig && stepConfig.key) {
      config = props.stepRegistry[stepConfig.key];
    }
  }

  if (!config) {
    log('WizardModal', 'resolveStepComponent', 'Step not found in registry', { stepKey, stepIndex });
    return null;
  }

  // Check visibility condition if provided
  if (config.condition && typeof config.condition === 'function') {
    if (!config.condition(props.wizardState)) {
      log('WizardModal', 'resolveStepComponent', 'Step condition not met', { stepKey, stepIndex });
      return null;
    }
  }

  // Resolve props and events from registry functions
  const resolvedProps = config.getProps && typeof config.getProps === 'function'
    ? config.getProps(props.wizardState, stepIndex)
    : {};

  const resolvedEvents = config.getEvents && typeof config.getEvents === 'function'
    ? config.getEvents(props.wizardState, stepIndex, props.eventHandlers)
    : {};

  return {
    component: config.component,
    props: resolvedProps,
    events: resolvedEvents
  };
};

/**
 * Navigate to step by key (in addition to index-based navigation)
 *
 * @param stepKey - Step key to navigate to
 * @returns True if navigation succeeded, false otherwise
 */
const goToStepByKey = (stepKey: string): boolean => {
  if (!stepKey) {
    log('WizardModal', 'goToStepByKey', 'No step key provided');
    return false;
  }

  // Find step index by key
  const stepIndex = props.steps.findIndex(step =>
    step.key === stepKey || step.title === stepKey
  );

  if (stepIndex === -1) {
    log('WizardModal', 'goToStepByKey', 'Step key not found', { stepKey });
    return false;
  }

  return wizard.goToStep(stepIndex);
};

// Watch for external modal close
watch(() => props.showModal, (newValue: boolean, oldValue: boolean) => {
  if (newValue && !oldValue) {
    emit('open');
  } else if (!newValue && oldValue) {
    if (props.resetOnClose) {
      wizard.reset();
    }
  }
});

// Expose methods for parent access
defineExpose({
  // Wizard state
  currentStep: wizard.currentStep,
  totalSteps: wizard.totalSteps,
  isFirstStep: wizard.isFirstStep,
  isLastStep: wizard.isLastStep,

  // Wizard methods
  goToStep: wizard.goToStep,
  goToStepByKey: goToStepByKey,
  nextStep: wizard.nextStep,
  previousStep: wizard.previousStep,
  validateCurrentStep: wizard.validateCurrentStep,
  reset: wizard.reset,
  complete: wizard.complete
});
</script>

<style scoped>
.wizard-modal-content {
  padding: 20px;
  background-color: #FFFFFF;
  border-radius: 8px;
  margin-bottom: 20px;
  /* Garantir que o conteúdo seja clicável */
  pointer-events: auto;
}

.wizard-modal-container {
  padding: 0;
  overflow-x: hidden;
  max-width: 100%;
  /* Garantir que o conteúdo seja clicável */
  pointer-events: auto;
}

/* Built-in Summary Styling */
.wizard-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-left: 4px solid var(--app-info-color);
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
}

.summary-item i {
  font-size: 0.875rem;
  color: var(--app-info-color);
  flex-shrink: 0;
}

.summary-label {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}

.summary-value {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
}

/* Responsive */
@media (max-width: 768px) {
  .wizard-modal-container {
    padding: 0;
  }

  .wizard-summary {
    padding: 10px 12px;
  }

  .summary-row {
    gap: 12px;
  }

  .summary-item {
    font-size: 0.8125rem;
  }
}
</style>
